<script setup lang="ts">
import { MateSpriteDirection } from "@/enums/game";
import { MateSprite } from "@/types/game";
import { onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps<{
  targetSprite: MateSprite;
}>();

// 常量定义
const FRAME_SIZE = 48;
const FRAME_DURATION = 150;
// const IDLE_DURATION = 2000; // IDLE状态持续时间
// const WALK_DURATION = 3000; // WALK状态持续时间
const SPRITE_SPEED = 2; // 移动速度（像素/帧）

// 随机持续时间函数
const getRandomIdleDuration = () => {
  return 1500 + Math.random() * 2000; // 1.5-3.5秒随机
};

const getRandomWalkDuration = () => {
  return 2000 + Math.random() * 3000; // 2-5秒随机
};

// 获取随机初始位置
const getRandomInitialPosition = () => {
  // 获取一个合理的随机范围，避免角色出现在边缘
  const minPos = 50;
  const maxX = window.innerWidth - FRAME_SIZE - 50;
  const maxY = window.innerHeight - FRAME_SIZE - 50;

  return {
    x: minPos + Math.random() * (maxX - minPos),
    y: minPos + Math.random() * (maxY - minPos),
  };
};

// 状态枚举
enum SpriteState {
  IDLE = "idle",
  WALK = "walk",
}

const spriteContainer = ref<HTMLDivElement | null>(null);

// 精灵状态
const currentState = ref<SpriteState>(SpriteState.IDLE);
const currentDirection = ref<MateSpriteDirection>(MateSpriteDirection.Down);
const currentFrame = ref(0);
const position = ref(getRandomInitialPosition()); // 使用随机初始位置
const rotation = ref(0);

// 拖动状态
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });
const lastDragTime = ref(0);

// 菜单状态
// const menuPosition = ref({ x: 0, y: 0 });
const isMenuMode = ref(false); // 菜单模式标记

// 点击检测状态
const clickStartTime = ref(0);
const clickStartPosition = ref({ x: 0, y: 0 });
// const CLICK_THRESHOLD_TIME = 200; // 200ms内算作点击
// const CLICK_THRESHOLD_DISTANCE = 5; // 5像素内算作点击

// 动画状态
const animationState = ref({
  frameId: 0,
  lastFrameTime: 0,
  lastStateChangeTime: 0,
});

// 更新精灵位置
const updatePosition = () => {
  // 如果正在拖动或菜单模式，不执行自动移动
  if (
    currentState.value !== SpriteState.WALK ||
    isDragging.value ||
    isMenuMode.value
  )
    return;

  const containerRect = spriteContainer.value?.getBoundingClientRect();

  if (!containerRect) return;

  const maxX = containerRect.width - FRAME_SIZE;
  const maxY = containerRect.height - FRAME_SIZE * 2;

  let newX = position.value.x;
  let newY = position.value.y;
  let newDirection = currentDirection.value;
  let newRotation = 0;

  // 根据当前方向更新位置
  switch (currentDirection.value) {
    case MateSpriteDirection.Up:
      newY -= SPRITE_SPEED;
      if (newY <= 0) {
        newY = 0;
        newDirection = MateSpriteDirection.Right;
        newRotation = 0;
      }
      break;
    case MateSpriteDirection.Down:
      newY += SPRITE_SPEED;
      if (newY >= maxY) {
        newY = maxY;
        newDirection = MateSpriteDirection.Left;
        newRotation = 0;
      }
      break;
    case MateSpriteDirection.Left:
      newX -= SPRITE_SPEED;
      newRotation = 0;
      if (newX <= 0) {
        newX = 0;
        newDirection = MateSpriteDirection.Up;
        newRotation = 0;
      }
      break;
    case MateSpriteDirection.Right:
      newX += SPRITE_SPEED;
      newRotation = 0;
      if (newX >= maxX) {
        newX = maxX;
        newDirection = MateSpriteDirection.Down;
        newRotation = 0;
      }
      break;
  }

  position.value = { x: newX, y: newY };
  currentDirection.value = newDirection;
  rotation.value = newRotation;
};

// 状态切换逻辑
const updateState = (timestamp: number) => {
  // 如果正在拖动或菜单模式，不切换状态
  if (isDragging.value || isMenuMode.value) return;

  const timeSinceStateChange =
    timestamp - animationState.value.lastStateChangeTime;

  if (
    currentState.value === SpriteState.IDLE &&
    timeSinceStateChange > currentIdleDuration.value
  ) {
    // 从IDLE切换到WALK
    currentState.value = SpriteState.WALK;
    currentFrame.value = 0;
    // 随机选择一个移动方向
    const directions = [
      MateSpriteDirection.Up,
      MateSpriteDirection.Down,
      MateSpriteDirection.Left,
      MateSpriteDirection.Right,
    ];
    currentDirection.value =
      directions[Math.floor(Math.random() * directions.length)];
    animationState.value.lastStateChangeTime = timestamp;
    // 生成新的walk持续时间
    currentWalkDuration.value = getRandomWalkDuration();
  } else if (
    currentState.value === SpriteState.WALK &&
    timeSinceStateChange > currentWalkDuration.value
  ) {
    // 从WALK切换到IDLE
    currentState.value = SpriteState.IDLE;
    currentFrame.value = 0;
    currentDirection.value = MateSpriteDirection.Down;
    rotation.value = 0;
    animationState.value.lastStateChangeTime = timestamp;
    // 生成新的idle持续时间
    currentIdleDuration.value = getRandomIdleDuration();
  }
};

// 拖动事件处理
const handleDragStart = (clientX: number, clientY: number) => {
  isDragging.value = true;
  lastDragTime.value = Date.now();

  // 记录点击开始时间和位置
  clickStartTime.value = Date.now();
  clickStartPosition.value = { x: clientX, y: clientY };

  // 计算鼠标相对于精灵的偏移
  dragOffset.value = {
    x: clientX - position.value.x,
    y: clientY - position.value.y,
  };

  // 切换到IDLE状态并重置旋转
  currentState.value = SpriteState.IDLE;
  currentFrame.value = 0;
  rotation.value = 0;

  // 拖拽开始时绘制一次静态帧
};

// 节流处理
let dragMoveThrottle = false;

const handleDragMove = (clientX: number, clientY: number) => {
  if (!isDragging.value || !spriteContainer.value || dragMoveThrottle) return;

  // 节流：限制更新频率
  dragMoveThrottle = true;
  requestAnimationFrame(() => {
    dragMoveThrottle = false;
  });

  const containerRect = spriteContainer.value.getBoundingClientRect();
  const maxX = containerRect.width - FRAME_SIZE;
  const maxY = containerRect.height - FRAME_SIZE * 2;

  // 计算新位置
  let newX = clientX - dragOffset.value.x;
  let newY = clientY - dragOffset.value.y;

  // 限制在容器范围内
  newX = Math.max(0, Math.min(newX, maxX));
  newY = Math.max(0, Math.min(newY, maxY));

  position.value = { x: newX, y: newY };
};

const handleDragEnd = () => {
  if (!isDragging.value) return;

  isDragging.value = false;

  animationState.value.lastStateChangeTime = performance.now();
};

// 鼠标事件
const handleMouseDown = (event: MouseEvent) => {
  event.preventDefault();
  handleDragStart(event.clientX, event.clientY);
};

const handleMouseMove = (event: MouseEvent) => {
  handleDragMove(event.clientX, event.clientY);
};

const handleMouseUp = () => {
  handleDragEnd();
};

// 触摸事件
const handleTouchStart = (event: TouchEvent) => {
  event.preventDefault();
  if (event.touches.length > 0) {
    handleDragStart(event.touches[0].clientX, event.touches[0].clientY);
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (event.touches.length > 0) {
    handleDragMove(event.touches[0].clientX, event.touches[0].clientY);
  }
};

const handleTouchEnd = (event: TouchEvent) => {
  // 使用changedTouches获取结束的触摸点
  if (event.changedTouches.length > 0) {
    handleDragEnd();
  } else {
    handleDragEnd();
  }
};

// 主动画循环
const animate = (timestamp: number) => {
  if (!animationState.value.lastStateChangeTime) {
    animationState.value.lastStateChangeTime = timestamp;
  }

  // 更新状态
  updateState(timestamp);

  // 更新帧动画
  if (!animationState.value.lastFrameTime) {
    animationState.value.lastFrameTime = timestamp;
  }

  const deltaTime = timestamp - animationState.value.lastFrameTime;
  if (deltaTime >= FRAME_DURATION) {
    updatePosition();
    animationState.value.lastFrameTime = timestamp;
  }

  animationState.value.frameId = requestAnimationFrame(animate);
};

// 开始动画
const startAnimation = () => {
  if (animationState.value.frameId) {
    cancelAnimationFrame(animationState.value.frameId);
  }
  animationState.value.frameId = requestAnimationFrame(animate);
};

// 动态持续时间
const currentIdleDuration = ref(getRandomIdleDuration());
const currentWalkDuration = ref(getRandomWalkDuration());

// 生命周期
onMounted(async () => {
  spriteContainer.value = document.querySelector(".sprite-container");

  if (props.targetSprite) {
    startAnimation();
  }

  // 添加全局事件监听器
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  document.addEventListener("touchmove", handleTouchMove);
  document.addEventListener("touchend", handleTouchEnd);
});

onUnmounted(() => {
  // 移除全局事件监听器
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
  document.removeEventListener("touchmove", handleTouchMove);
  document.removeEventListener("touchend", handleTouchEnd);
});

// 监听目标精灵变化
watch(
  () => props.targetSprite,
  (newSprite) => {
    if (newSprite) {
      startAnimation();
    }
  }
);
</script>

<template>
  <div
    class="absolute pointer-events-auto cursor-grab"
    :class="{
      'cursor-grabbing': isDragging,
      'scale-110 drop-shadow-lg': isDragging,
    }"
    :style="{
      left: position.x + 'px',
      top: position.y + 'px',
      transform: `rotate(${rotation}deg)`,
      zIndex: isDragging ? 60 : 50,
      transition: isDragging ? 'none' : 'transform 0.2s ease-out',
    }"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
  >
    <SpriteFrame
      v-show="currentState === SpriteState.WALK"
      :key="currentDirection"
      :url="props.targetSprite.animationzz?.find(v=>v.direction === currentDirection)?.media?.url!"
      class="!w-9"
    />
    <SpriteImgList
      v-show="currentState === SpriteState.IDLE"
      :url="props.targetSprite.animationzz?.find(v=>v.direction === MateSpriteDirection.Down)?.media?.url!"
      :index="0"
      class="!w-9"
    />
  </div>
</template>
